import type { CustomField, PipelineMemberFlags, RuleOperator, RuleSource } from '../types';

/**
 * Espelho de `RULE_FIELDS` da API (stage-rules.ts): os campos fixos que as
 * condicionais enxergam e o tipo de cada um, que define os operadores (Q18).
 */
export type RuleFieldKind = 'number' | 'text' | 'list';

export const OPERATORS_BY_KIND: Record<RuleFieldKind, RuleOperator[]> = {
  number: ['exists', 'not_exists', 'eq', 'neq', 'gt', 'lt', 'gte', 'lte'],
  text: ['exists', 'not_exists', 'eq', 'neq', 'contains'],
  list: ['exists', 'not_exists', 'in', 'not_in'],
};

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  exists: 'está preenchido',
  not_exists: 'está vazio',
  eq: 'é igual a',
  neq: 'é diferente de',
  gt: 'maior que',
  lt: 'menor que',
  gte: 'maior ou igual a',
  lte: 'menor ou igual a',
  contains: 'contém',
  in: 'é um de',
  not_in: 'não é nenhum de',
};

export const SOURCE_LABELS: Record<RuleSource, string> = {
  business: 'Negócio',
  lead: 'Lead',
  business_cf: 'Campo adicional do negócio',
  lead_person_cf: 'Campo adicional do lead',
  lead_company_cf: 'Campo adicional da empresa',
};

/** Campos fixos: [chave, rótulo, tipo, de onde vêm as opções da lista]. */
export type ListOptionsSource = 'tags' | 'lists' | 'products' | 'attendants' | 'kind';

export const FIXED_FIELDS: Record<
  'lead' | 'business',
  Array<{ key: string; label: string; kind: RuleFieldKind; options?: ListOptionsSource }>
> = {
  business: [
    { key: 'total', label: 'Valor total (centavos)', kind: 'number' },
    { key: 'products', label: 'Produtos', kind: 'list', options: 'products' },
    { key: 'activities', label: 'Quantidade de atividades', kind: 'number' },
    { key: 'attendant', label: 'Atendente', kind: 'list', options: 'attendants' },
    { key: 'discount', label: 'Desconto (centavos)', kind: 'number' },
    { key: 'addition', label: 'Acréscimo (centavos)', kind: 'number' },
    { key: 'shipping', label: 'Frete (centavos)', kind: 'number' },
    { key: 'code', label: 'Número', kind: 'number' },
    { key: 'coupon', label: 'Cupom', kind: 'text' },
    { key: 'shippingType', label: 'Tipo de frete', kind: 'text' },
    { key: 'externalId', label: 'ID externo', kind: 'text' },
  ],
  lead: [
    { key: 'name', label: 'Nome', kind: 'text' },
    { key: 'email', label: 'E-mail', kind: 'text' },
    { key: 'phone', label: 'Telefone', kind: 'text' },
    { key: 'taxId', label: 'CPF/CNPJ', kind: 'text' },
    { key: 'source', label: 'Origem', kind: 'text' },
    { key: 'jobTitle', label: 'Cargo', kind: 'text' },
    { key: 'segment', label: 'Segmento', kind: 'text' },
    { key: 'kind', label: 'Tipo (Pessoa/Empresa)', kind: 'list', options: 'kind' },
    { key: 'attendant', label: 'Atendente do lead', kind: 'list', options: 'attendants' },
    { key: 'tags', label: 'Tags', kind: 'list', options: 'tags' },
    { key: 'lists', label: 'Listas', kind: 'list', options: 'lists' },
  ],
};

/** Tipo de um campo adicional para as condicionais. */
export function customFieldKind(field: Pick<CustomField, 'type'>): RuleFieldKind {
  if (field.type === 'NUMBER' || field.type === 'RATING') return 'number';
  if (field.type === 'SELECT' || field.type === 'MULTI_SELECT') return 'list';
  return 'text';
}

/** Entidade do campo adicional de cada origem. */
export const CF_ENTITY: Partial<Record<RuleSource, 'lead' | 'company' | 'business'>> = {
  business_cf: 'business',
  lead_person_cf: 'lead',
  lead_company_cf: 'company',
};

/** Minutos ↔ quantidade + unidade, para o vencimento das atividades por etapa. */
export const DUE_UNITS = [
  ['minutes', 'minuto(s)', 1],
  ['hours', 'hora(s)', 60],
  ['days', 'dia(s)', 1440],
] as const;

export function describeDue(minutes: number): string {
  if (minutes === 0) return 'na hora da entrada';
  for (const [, label, size] of [...DUE_UNITS].reverse()) {
    if (minutes % size === 0) return `${minutes / size} ${label} depois`;
  }
  return `${minutes} minuto(s) depois`;
}

/** As 4 travas de atendente, na ordem da aba Atendentes e da tela de Membros. */
export const FLAG_LABELS: Array<[keyof PipelineMemberFlags, string]> = [
  ['showOnlyMine', 'Só vê os próprios negócios'],
  ['disableCreate', 'Não pode criar negócios'],
  ['disableDelete', 'Não pode excluir negócios'],
  ['disableAttendantChange', 'Não pode trocar o atendente'],
];
